import React, { useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocaleChange } from '../../../../hooks/useLocaleChange';
import { getErrorMessage } from '../../../../api/utils/error-handler';
import { useSnackbar } from '../../../../contexts/SnackbarContext';
import { wikiNewApi } from '../../../../api/wiki-new.service';
import { useWikiItem } from '../../../../contexts/WikiItemContext';
import {
  WeaponDetailDto,
  ArmorDetailDto,
  ItemDetailDto,
  NpcDetailDto,
  SkillDetailDto
} from '../../../../types';

// Import specialized components
import WeaponDetail from './WeaponDetail';
import ArmorDetail from './ArmorDetail';
import ItemDetail from './ItemDetail';
import NpcDetail from './NpcDetail';
import SkillDetail from './SkillDetail';

interface DetailRouterProps {
  source: string;
  id: number;
  typeId?: number;
  subtypeId?: number;
  wikiSubCategory?: number;
  // Skills-specific props
  raceId?: number;
  skillTypeId?: number;
  classId?: number;
  professionId?: number;
}

type DetailData = WeaponDetailDto | ArmorDetailDto | ItemDetailDto | NpcDetailDto | SkillDetailDto[];

const DetailRouter: React.FC<DetailRouterProps> = ({
  source,
  id,
  typeId,
  subtypeId,
  wikiSubCategory,
  raceId,
  skillTypeId,
  classId,
  professionId
}) => {
  const { t } = useTranslation('wiki');
  const [data, setData] = useState<DetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const { setCurrentItemName, setIsItemLoading } = useWikiItem();
  const { showError } = useSnackbar();
  const [languageTrigger, setLanguageTrigger] = useState(0);

  // Reload detail when language changes
  useLocaleChange(useCallback(() => {
    setLanguageTrigger(prev => prev + 1);
  }, []));

  useEffect(() => {
    const loadDetailData = async () => {
      try {
        setLoading(true);
        setIsItemLoading(true);

        let result: DetailData;

        switch (source) {
          case 'weapons':
            result = await wikiNewApi.getWeaponDetail(id);
            break;
          case 'armors':
          case 'accessories':
          case 'jewelry':
            result = await wikiNewApi.getArmorDetail(id);
            break;
          case 'items':
          case 'recipes':
            result = await wikiNewApi.getItemDetail(id);
            break;
          case 'npcs':
            result = await wikiNewApi.getNpcDetail(id);
            break;
          case 'skills':
            // For skills, use class API if all 4 params available, otherwise regular API
            if (raceId && skillTypeId && classId && professionId) {
              result = await wikiNewApi.getSkillDetailByClass(id, raceId, skillTypeId, classId, professionId);
            } else {
              result = await wikiNewApi.getSkillDetail(id);
            }
            break;
          default:
            throw new Error(t('messages.unsupportedSource', { source }));
        }

        setData(result);

        // Set the item name for breadcrumbs
        if (result) {
          if (source === 'skills') {
            // Skills is an array, get the name from first skill
            const skillsArray = result as SkillDetailDto[];
            if (skillsArray.length > 0) {
              setCurrentItemName(skillsArray[0].name);
            }
          } else {
            // All other types have a name property
            const itemWithName = result as WeaponDetailDto | ArmorDetailDto | ItemDetailDto | NpcDetailDto;
            setCurrentItemName(itemWithName.name);
          }
        }

        setIsItemLoading(false);
      } catch (err: unknown) {
        console.error('Error loading detail data:', err);
        showError(getErrorMessage(err, t('messages.loadDetailError')));
        setIsItemLoading(false);
      } finally {
        setLoading(false);
      }
    };

    if (source && id) {
      loadDetailData();
    }

    // Cleanup: clear item name when component unmounts or ID changes
    return () => {
      setCurrentItemName(null);
      setIsItemLoading(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, id, raceId, skillTypeId, classId, professionId, setCurrentItemName, setIsItemLoading, languageTrigger]);


  const renderDetailComponent = () => {
    if (!data) return null;

    switch (source) {
      case 'weapons':
        return <WeaponDetail weapon={data as WeaponDetailDto} />;
      case 'armors':
      case 'accessories':
      case 'jewelry':
        return <ArmorDetail armor={data as ArmorDetailDto} />;
      case 'items':
      case 'recipes':
        return <ItemDetail item={data as ItemDetailDto} />;
      case 'npcs':
        return <NpcDetail npc={data as NpcDetailDto} />;
      case 'skills':
        return <SkillDetail skills={data as SkillDetailDto[]} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {renderDetailComponent()}
    </Box>
  );
};

export default DetailRouter;